export function buildBackstageRoutes({
  routes,
  AsyncDomain,
  onEnterPodcastBSP,
  onEnterCollectedStationBSP,
  onEnterCuratorBSP,
  onLeaveCuratorBSP,
  isCuratorExperienceEnabled,
  onEnterBackstage,
  onLeaveBackstage,
  onEnterBackstageLyrics,
  onEnterPlaylist,
  onLeavePlaylist,
  createConditionalComponent,
  isMobileWeb,
  redirectPlaybackToBackstage,
}) {
  const CANONICAL_GENRE_URL_PATH = 'genre/:genreSlug/:genreId';
  function buildBackstageRoute(path, componentName = 'Backstage', props) {
    const newProps = Object.assign(
      {},
      {
        domain: 'backstage',
        component: componentName,
        key: componentName,
      },
      props,
    );

    return {
      path,
      onEnter: onEnterBackstage,
      onLeave: onLeaveBackstage,
      props: newProps,
      component: AsyncDomain,
    };
  }

  function buildBackstageChildRoute(path, componentName) {
    const props = {
      domain: 'backstage',
      component: componentName,
      key: componentName,
    };

    return {
      path,
      props,
      component: AsyncDomain,
    };
  }

  function buildMobilePlaybackRedirectRoute(path) {
    return {
      path,
      onEnter: redirectPlaybackToBackstage,
      props: {
        domain: 'backstage',
        component: 'Backstage',
        key: 'Backstage',
      },
      component: AsyncDomain,
    };
  }

  function buildBackstagePodcastRoute(path, componentName) {
    return {
      path,
      onEnter: onEnterPodcastBSP,
      props: {
        domain: 'podcast',
        component: componentName,
        key: componentName,
      },
      component: AsyncDomain,
    };
  }

  function buildBackstageCollectedStationRoute(path, componentName) {
    return {
      path,
      onEnter: onEnterCollectedStationBSP,
      props: {
        domain: 'playback',
        component: componentName,
        key: componentName,
      },
      component: AsyncDomain,
    };
  }

  const mobilePlaybackRedirectRoutes = [];

  if (isMobileWeb) {
    mobilePlaybackRedirectRoutes.push(
      buildMobilePlaybackRedirectRoute('artist/play/:token1/:pandoraId'),
      buildMobilePlaybackRedirectRoute('artist/play/:token1/:token2/:pandoraId'),
      buildMobilePlaybackRedirectRoute('artist/play/:token1/:token2/:token3/:pandoraId'),
      buildMobilePlaybackRedirectRoute('playlist/play/:pandoraId'),
    );
  }

  const standardRoutes = [
    {
      path: 'artist',
      component: AsyncDomain,
      onEnter: onEnterBackstage,
      onLeave: onLeaveBackstage,
      props: {
        domain: 'backstage',
        component: 'Backstage',
        key: 'Backstage',
      },
      indexRoute: buildBackstageChildRoute(null, 'BackstageArtistHome'),
      childRoutes: [
        buildBackstageChildRoute('all-songs/:seoToken1', 'BackstageArtistAllSongs'),
        buildBackstageChildRoute('all-songs/:seoToken1/:pandoraId', 'BackstageArtistAllSongs'),
        buildBackstageChildRoute('all-albums/:seoToken1', 'BackstageArtistAllAlbums'),
        buildBackstageChildRoute('all-albums/:seoToken1/:pandoraId', 'BackstageArtistAllAlbums'),
        buildBackstageChildRoute('all-stations/:seoToken1', 'BackstageAllStations'),
        buildBackstageChildRoute('all-stations/:seoToken1/:pandoraId', 'BackstageAllStations'),
        buildBackstageChildRoute('all-playlists/:seoToken1', 'BackstageAllPlaylists'),
        buildBackstageChildRoute('all-playlists/:seoToken1/:pandoraId', 'BackstageAllPlaylists'),
        buildBackstageChildRoute('full-bio/:seoToken1', 'BackstageFullDescription'),
        buildBackstageChildRoute('full-bio/:seoToken1/:pandoraId', 'BackstageFullDescription'),
        buildBackstageChildRoute('description/:seoToken1/:seoToken2', 'BackstageFullDescription'),
        buildBackstageChildRoute(
          'description/:seoToken1/:seoToken2/:pandoraId',
          'BackstageFullDescription',
        ),
        buildBackstageChildRoute(
          'similar-songs/:seoToken1/:seoToken2/:seoToken3',
          'BackstageTrackSimilarSongs',
        ),
        buildBackstageChildRoute(
          'similar-songs/:seoToken1/:seoToken2/:seoToken3/:pandoraId',
          'BackstageTrackSimilarSongs',
        ),
        {
          path: 'lyrics/:seoToken1/:seoToken2/:seoToken3',
          onEnter: onEnterBackstageLyrics,
          props: {
            domain: 'backstage',
            component: 'BackstageTrackFullLyrics',
            key: 'BackstageTrackFullLyrics',
          },
          component: AsyncDomain,
        },
        {
          path: 'lyrics/:seoToken1/:seoToken2/:seoToken3/:pandoraId',
          onEnter: onEnterBackstageLyrics,
          props: {
            domain: 'backstage',
            component: 'BackstageTrackFullLyrics',
            key: 'BackstageTrackFullLyrics',
          },
          component: AsyncDomain,
        },
        buildBackstageChildRoute(
          'credits/:seoToken1/:seoToken2/:seoToken3',
          'BackstageTrackFullCredits',
        ),
        buildBackstageChildRoute(
          'credits/:seoToken1/:seoToken2/:seoToken3/:pandoraId',
          'BackstageTrackFullCredits',
        ),
      ],
    },
    buildBackstageRoute('artist/:seoToken1/:seoToken2'),
    buildBackstageRoute('artist/:seoToken1/:seoToken2/:seoToken3'),
    buildBackstageRoute('artist/:seoToken1/:seoToken2/:seoToken3/:seoToken4'),
    buildBackstageRoute(CANONICAL_GENRE_URL_PATH, 'BackstageGenre'),
    buildBackstageRoute('genre/:genreSlug', 'BackstageGenre'),
    {
      path: 'curator',
      onEnter: onEnterCuratorBSP,
      onLeave: onLeaveCuratorBSP,
      getComponent(location, cb) {
        const conditionalComponent = createConditionalComponent({
          condition: isCuratorExperienceEnabled,
          ifTrueOptions: {
            domain: 'backstage',
            component: 'Backstage',
            key: 'BackstageCurator',
          },
        });

        cb(null, conditionalComponent);
      },
      getIndexRoute(location, cb) {
        const conditionalComponent = createConditionalComponent({
          condition: isCuratorExperienceEnabled,
          ifTrueOptions: {
            domain: 'backstage',
            component: 'Backstage',
            key: 'BackstageCurator',
          },
        });

        cb(null, { component: conditionalComponent });
      },
      childRoutes: [
        buildBackstageChildRoute('all-playlists/:seoToken1/:pandoraId', 'BackstageAllPlaylists'),
        buildBackstageChildRoute('full-bio/:seoToken1/:pandoraId', 'BackstageFullDescription'),
        buildBackstageChildRoute('all-stations/:seoToken1/:pandoraId', 'BackstageAllStations'),
      ],
    },
    {
      path: 'curator/:seoToken1/:pandoraId',
      onEnter: onEnterCuratorBSP,
      onLeave: onLeaveCuratorBSP,
      getComponent(location, cb) {
        const conditionalComponent = createConditionalComponent({
          condition: isCuratorExperienceEnabled,
          ifTrueOptions: {
            domain: 'backstage',
            component: 'Backstage',
            key: 'BackstageCurator',
          },
        });

        cb(null, conditionalComponent);
      },
      getIndexRoute(location, cb) {
        const conditionalComponent = createConditionalComponent({
          condition: isCuratorExperienceEnabled,
          ifTrueOptions: {
            domain: 'backstage',
            component: 'Backstage',
            key: 'BackstageCurator',
          },
        });

        cb(null, { component: conditionalComponent });
      },
    },
    {
      path: 'playlist/:playlistId',
      onEnter: onEnterPlaylist,
      onLeave: onLeavePlaylist,
      props: {
        domain: 'playlistT3',
        component: 'BackstagePlaylist',
        key: 'BackstagePlaylist',
      },
      component: AsyncDomain,
    },
    buildBackstagePodcastRoute(
      'podcast/all-episodes/:programName/:podcastId',
      'BackstagePodcastProgramAllEpisodes',
    ),
    buildBackstagePodcastRoute(
      'podcast/thumbed-up-episodes/:programName/:podcastId',
      'BackstagePodcastProgramAllThumbs',
    ),
    buildBackstagePodcastRoute(
      'podcast/thumbed-down-episodes/:programName/:podcastId',
      'BackstagePodcastProgramAllThumbs',
    ),
    buildBackstagePodcastRoute(
      'podcast/description/:programName/:podcastId',
      'BackstagePodcastProgramFullDescription',
    ),
    buildBackstagePodcastRoute(
      'podcast/description/:programName/:episodeName/:podcastId',
      'BackstagePodcastEpisodeFullDescription',
    ),
    buildBackstagePodcastRoute(
      'podcast/transcript/:programName/:episodeName/:podcastId',
      'BackstagePodcastEpisodeTranscript',
    ),
    buildBackstagePodcastRoute(
      'podcast/all-episodes/:programName/:podcastId',
      'BackstagePodcastProgramAllEpisodes',
    ),
    buildBackstagePodcastRoute('podcast/:programName/:podcastId', 'BackstagePodcastProgram'),
    buildBackstagePodcastRoute(
      'podcast/:programName/:episodeName/:podcastId',
      'BackstagePodcastEpisode',
    ),
    buildBackstageCollectedStationRoute('station/:stationId', 'BackstageCollectedStation'),
    buildBackstageCollectedStationRoute(
      'station/thumbed-up/:stationId',
      'BackstageCollectedStationAllThumbs',
    ),
    buildBackstageCollectedStationRoute(
      'station/thumbed-down/:stationId',
      'BackstageCollectedStationAllThumbs',
    ),
    buildBackstageCollectedStationRoute(
      'station/seeds/:stationId',
      'BackstageCollectedStationSeeds',
    ),
  ];

  return routes.concat(mobilePlaybackRedirectRoutes, standardRoutes);
}

export default {
  buildBackstageRoutes,
};
